export type TaskScopeVisit = {
  type: '.Visit';
  visitDefinitions: string[];
  allRegular: boolean;
  unscheduled: boolean;
};

export type TaskScopePatient = {
  type: '.Patient';
  cohorts: string[];
};

export type ComparisonType = 'ALL' | 'ANY' | 'NONE';

export type TaskDefinitionScope = TaskScopeVisit | TaskScopePatient;
export type CollectionElementType =
  | CollectionElementTypeStatus
  | CollectionElementTypeResult
  | CollectionElementTypeStatuses
  | { type: '.All' };

export type TaskDefinitionOption = {
  type: '.And' | '.Or' | '.Not' | '.Tag';
  conditions: TaskDefinitionTag[] | TaskDefinitionOption[];
};

export type TaskDefinitionTag = {
  type: '.Tag';
  scope: string;
  collectionType: string;
  conditions?: TaskDefinitionTag[] | TaskDefinitionOption[];
  collectionElementType: CollectionElementType;
  propertyName: string | null;
  expected: {
    type: string;
    comparisonType?: ComparisonType;
    expected?: string | string[];
  };
};

export type CollectionElementTypeStatus = {
  type: '.TagIdStatus';
  id: string;
  status: 'COMPLETED' | 'FAILED' | 'IN_PROGRESS';
};
export type CollectionElementTypeResult = {
  type: '.TagIdResultProperty';
  id: string;
  propertyValue: string;
  propertyPath: string;
  propertyOperator?: string;
  status: 'COMPLETED' | 'FAILED' | 'IN_PROGRESS';
};
export type CollectionElementTypeStatuses = {
  type: '.TagIdStatuses';
  id: string;
  statuses: TaskStatuses[];
};

export type TaskDefinitionActionArgument = {
  scope: TagConditionScope;
  collectionType: CollectionTagEnum;
  collectionElementType: CollectionElementType;
};

type BaseActionItem = {
  tag: string;
  tagId: string;
  tagScope: TagConditionScope.PATIENT | TagConditionScope.CURRENT_VISIT;
  arguments: TaskDefinitionActionArgument[];
  metadata: Record<string, string>;
  initialStatus: TaskStatuses;
};

export type ActionReportItem = BaseActionItem & {
  type: '.Report';
  templateId: string;
};

export type ActionScoringItem = BaseActionItem & {
  type: '.Scoring';
  readerId: string;
  scoringFormId: string;
};

export type ActionFunctionItem = BaseActionItem & {
  type: '.Function';
  functionId: string;
};

export type TaskDefinitionActionType = ActionReportItem | ActionScoringItem | ActionFunctionItem;

export type TaskDefinitionActions = {
  actions: TaskDefinitionActionType[];
};

export type PMTaskDefinition = {
  id: string;
  name: string;
  code: string;
  description: string;
  automaticExecution: boolean;
  taskDefinitionOrder: number;
  visibleForImagingSpecialist: boolean;
  scope: TaskDefinitionScope;
  condition: TaskDefinitionOption | TaskDefinitionTag;
  actions: TaskDefinitionActions;
};

export enum TaskStatuses {
  CREATED = 'CREATED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  READING_PENDING = 'READING_PENDING',
  TERMINATED = 'TERMINATED',
  PREPROCESSING_PENDING = 'PREPROCESSING_PENDING',
}

export enum TagConditionScope {
  PATIENT = 'PATIENT',
  ANY_VISIT = 'ANY_VISIT',
  CURRENT_VISIT = 'CURRENT_VISIT',

  PREVIOUS_VISIT = 'PREVIOUS_VISIT',
  ANY_PREVIOUS_VISITS = 'ANY_PREVIOUS_VISITS',
  ALL_PREVIOUS_VISITS = 'ALL_PREVIOUS_VISITS',
  NONE_PREVIOUS_VISITS = 'NONE_PREVIOUS_VISITS',
  ALL_PREVIOUS_VISITS_AND_CURRENT = 'ALL_PREVIOUS_VISITS_AND_CURRENT',

  NEXT_VISIT = 'NEXT_VISIT',
  ANY_NEXT_VISITS = 'ANY_NEXT_VISITS',
  ALL_NEXT_VISITS = 'ALL_NEXT_VISITS',
  NONE_NEXT_VISITS = 'NONE_NEXT_VISITS',
  ALL_NEXT_VISITS_AND_CURRENT = 'ALL_NEXT_VISITS_AND_CURRENT',

  ANY_INSCOPE_VISIT = 'ANY_INSCOPE_VISIT',
  ALL_INSCOPE_VISITS = 'ALL_INSCOPE_VISITS',
  NONE_INSCOPE_VISITS = 'NONE_INSCOPE_VISITS',

  INSCOPE_PREVIOUS_VISIT = 'INSCOPE_PREVIOUS_VISIT',
  ALL_INSCOPE_PREVIOUS_VISITS = 'ALL_INSCOPE_PREVIOUS_VISITS',
  ANY_INSCOPE_PREVIOUS_VISITS = 'ANY_INSCOPE_PREVIOUS_VISITS',
  NONE_INSCOPE_PREVIOUS_VISITS = 'NONE_INSCOPE_PREVIOUS_VISITS',
  ALL_INSCOPE_PREVIOUS_VISITS_AND_CURRENT = 'ALL_INSCOPE_PREVIOUS_VISITS_AND_CURRENT',

  INSCOPE_NEXT_VISIT = 'INSCOPE_NEXT_VISIT',
  ALL_INSCOPE_NEXT_VISITS = 'ALL_INSCOPE_NEXT_VISITS',
  ANY_INSCOPE_NEXT_VISITS = 'ANY_INSCOPE_NEXT_VISITS',
  NONE_INSCOPE_NEXT_VISITS = 'NONE_INSCOPE_NEXT_VISITS',
  ALL_INSCOPE_NEXT_VISITS_AND_CURRENT = 'ALL_INSCOPE_NEXT_VISITS_AND_CURRENT',
}

/**
 * Sources a Task Definition condition or action argument can read from.
 *
 * Plain terms
 * -----------
 * When someone configures a Task Definition they first pick where the data
 * comes from — a previous task's result, a DICOM label, a DXA label, and now
 * an uploaded patient file.
 *
 * Technical
 * ---------
 * Members that are NOT in `taskSet` (see `conditionOptions.ts`) are resolved
 * against study labels via `groupLabelsByFileType(labels)[collectionType]`,
 * which indexes on `CommonInventoryType.dataType`. Those members must therefore
 * share their spelling with `UploadFileTypeEnum` — that is why `DICOM` and
 * `DXA` work. `FILE` and `JSON` (D7-7610) depend on the backend emitting
 * labels with the matching `dataType`; until it does, their label pickers
 * render empty.
 */
export enum CollectionTagEnum {
  TASK = 'TASK',
  DICOM = 'DICOM',
  DXA = 'DXA',
  MEASUREMENT = 'MEASUREMENT',
  FILE = 'FILE',
  JSON = 'JSON',
}

export enum ActionTypeEnum {
  Report = '.Report',
  Scoring = '.Scoring',
  Function = '.Function',
}
